import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import { useGetCartoesSalvosQuery, useGetPedidoStatusQuery, usePagarPedidoOnlineMutation, type DadosPixCheckout } from '../api';
import type { RootState } from '../store';
import { formatarBRL, formatarTempoRestante } from '../lib/formato';

type Fase = 'iniciando' | 'pix' | 'cartao' | 'expirado' | 'erro';

/** Situações que indicam que o pedido foi pago (saiu de AGUARDANDO_PAGAMENTO). */
const SITUACOES_PAGAS = ['ENVIADO', 'CONFIRMADO', 'AGUARDANDO_RETIRADA', 'ENTREGUE', 'FALHA_ENVIO'];
/** Status de transação que liberam o pedido (cartão pré-autorizado já basta). */
const STATUS_PAGOS = ['AUTHORIZED', 'CONFIRMED', 'RECEIVED'];

/**
 * Pagamento online (ASAAS) no checkout do cliente (delivery).
 *  - PIX: dispara a cobrança (/pagar), exibe QR + copia-e-cola e faz polling do status.
 *  - CARTÃO (crédito): formulário; os dados vão ao /pagar (backend cobra no ASAAS via
 *    `creditCard`/`creditCardHolderInfo`, PCI SAQ-D). Pré-autoriza; recusa é retentável
 *    com outro cartão (spec §5.6). PAN/CVV não são persistidos.
 */
export function DialogPagamentoOnline({
  aberto,
  pedidoId,
  valorCentavos,
  metodos,
  onSucesso,
  onCancelar,
  onRefazer,
  onOutraForma,
  onFechar,
  ocupado = false,
}: {
  aberto: boolean;
  pedidoId: string;
  valorCentavos: number;
  metodos: string[];
  onSucesso: () => void;
  onCancelar: () => void;
  /** Pedido morto (cancelado/expirado): fecha mantendo o carrinho p/ refazer. */
  onRefazer: () => void;
  /** Desistir deste pagamento e voltar com o carrinho; sem ele o botão não aparece. */
  onOutraForma?: () => void;
  /** Toque fora/ESC só fecha o diálogo; sem ele, vale como "Cancelar pedido". */
  onFechar?: () => void;
  /** Quem abriu está cancelando o pedido: trava os botões de saída. */
  ocupado?: boolean;
}): JSX.Element {
  const [fase, setFase] = useState<Fase>('iniciando');
  const [pix, setPix] = useState<DadosPixCheckout | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [pagar, statusPagar] = usePagarPedidoOnlineMutation();
  const iniciado = useRef(false);

  // Campos do cartão (só quando a forma é cartão).
  const [numeroCartao, setNumeroCartao] = useState('');
  const [nomeCartao, setNomeCartao] = useState('');
  const [validade, setValidade] = useState('');
  const [cvv, setCvv] = useState('');

  // Cartão salvo: número = id selecionado; 'novo' = digitar outro; null = ainda não escolheu.
  const [cartaoSel, setCartaoSel] = useState<number | 'novo' | null>(null);
  const [salvarCartao, setSalvarCartao] = useState(false);

  const temPix = metodos.includes('PIX');

  // Cartões salvos do cliente (só se logado e na etapa de cartão). Token nunca vem pro front.
  const logado = useSelector((s: RootState) => !!s.auth.accessToken);
  const { data: cartoesSalvos } = useGetCartoesSalvosQuery(undefined, {
    skip: !aberto || fase !== 'cartao' || !logado,
  });
  const temCartoesSalvos = (cartoesSalvos?.length ?? 0) > 0;
  const usandoNovoCartao = cartaoSel === 'novo' || !temCartoesSalvos;

  // Polling do status do pedido enquanto o PIX está na tela.
  const { data: statusPedido } = useGetPedidoStatusQuery(pedidoId, {
    skip: !aberto || fase !== 'pix',
    pollingInterval: 4000,
  });
  const situacao = statusPedido?.situacao;

  // Contagem regressiva do PIX: o backend cancela o pedido no prazo e devolve o
  // instante-limite (`expiraPagamentoEm`). Mostra pro cliente não ficar no escuro.
  const expiraMs = statusPedido?.expiraPagamentoEm ? new Date(statusPedido.expiraPagamentoEm).getTime() : null;
  const [agoraMs, setAgoraMs] = useState(() => Date.now());
  useEffect(() => {
    if (fase !== 'pix' || !expiraMs) return;
    setAgoraMs(Date.now());
    const t = window.setInterval(() => setAgoraMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [fase, expiraMs]);
  const restantePixSeg = expiraMs ? Math.max(0, Math.round((expiraMs - agoraMs) / 1000)) : null;

  async function iniciarPix(): Promise<void> {
    setFase('iniciando');
    setMensagem(null);
    try {
      const r = await pagar({ idPedido: pedidoId, metodo: 'PIX' }).unwrap();
      setPix(r.pix ?? null);
      setFase('pix');
    } catch (err) {
      // Pedido já cancelado (ex.: timeout): não dá pra pagar — orienta refazer.
      if (extrairCodigo(err) === 'PEDIDO_CANCELADO') {
        setFase('expirado');
        return;
      }
      setMensagem(extrairMsg(err) ?? 'Não foi possível gerar o PIX. Tente novamente.');
      setFase('erro');
    }
  }

  useEffect(() => {
    if (!aberto) {
      iniciado.current = false;
      return;
    }
    if (iniciado.current) return;
    iniciado.current = true;
    if (temPix) void iniciarPix();
    else setFase('cartao');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, temPix]);

  // Reage ao status do polling: pago → sucesso; cancelado (timeout) → expirado.
  useEffect(() => {
    if (fase !== 'pix' || !situacao) return;
    if (situacao === 'CANCELADO') {
      setFase('expirado');
      return;
    }
    if (SITUACOES_PAGAS.includes(situacao)) onSucesso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [situacao, fase]);

  // Etapa de cartão: pré-seleciona o primeiro cartão salvo (ou "novo" se não houver).
  useEffect(() => {
    if (fase !== 'cartao' || cartaoSel !== null || cartoesSalvos === undefined) return;
    const primeiro = cartoesSalvos[0];
    setCartaoSel(primeiro ? primeiro.id : 'novo');
  }, [fase, cartoesSalvos, cartaoSel]);

  async function copiar(): Promise<void> {
    if (!pix?.payload) return;
    try {
      await navigator.clipboard.writeText(pix.payload);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      /* clipboard indisponível — o cliente pode selecionar manualmente */
    }
  }

  const numeroLimpo = numeroCartao.replace(/\D/g, '');
  const validadeOk = /^\d{2}\/\d{2}$/.test(validade) && Number(validade.slice(0, 2)) >= 1 && Number(validade.slice(0, 2)) <= 12;
  const cartaoValido =
    numeroLimpo.length >= 13 && numeroLimpo.length <= 19 && nomeCartao.trim().length > 1 && validadeOk && /^\d{3,4}$/.test(cvv);
  const podePagarCartao = usandoNovoCartao ? cartaoValido : typeof cartaoSel === 'number';

  async function pagarCartao(): Promise<void> {
    if (!podePagarCartao) return;
    setMensagem(null);
    try {
      let r;
      if (usandoNovoCartao) {
        const validadeMes = validade.slice(0, 2);
        const validadeAno = `20${validade.slice(3, 5)}`;
        r = await pagar({
          idPedido: pedidoId,
          metodo: 'CREDIT_CARD',
          cartao: { numero: numeroLimpo, nome: nomeCartao.trim(), validadeMes, validadeAno, cvv },
          // Tokeniza e salva só se logado e o cliente marcou (opt-in).
          ...(logado && salvarCartao ? { salvarCartao: true } : {}),
        }).unwrap();
      } else {
        // Cartão salvo: cobra pelo token (sem PAN/CVV).
        r = await pagar({ idPedido: pedidoId, metodo: 'CREDIT_CARD', idCartaoSalvo: cartaoSel as number }).unwrap();
      }
      if (STATUS_PAGOS.includes(r.status)) {
        onSucesso();
        return;
      }
      setMensagem('Pagamento não aprovado. Confira os dados ou tente outro cartão.');
    } catch (err) {
      // Pedido já cancelado (ex.: timeout enquanto preenchia o cartão): refazer.
      if (extrairCodigo(err) === 'PEDIDO_CANCELADO') {
        setFase('expirado');
        return;
      }
      setMensagem(extrairMsg(err) ?? 'Não foi possível processar o pagamento. Tente outro cartão.');
    }
  }

  const botaoOutraForma = onOutraForma ? (
    <Button variant="outlined" onClick={onOutraForma} disabled={statusPagar.isLoading || ocupado}>
      Trocar forma de pagamento
    </Button>
  ) : null;

  // Cartão em processamento: fechar no meio deixaria o cliente sem saber se pagou.
  const processandoCartao = fase === 'cartao' && statusPagar.isLoading;

  return (
    <Dialog
      open={aberto}
      onClose={ocupado || processandoCartao ? undefined : (onFechar ?? onCancelar)}
      fullWidth
      maxWidth="xs"
    >
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography variant="h6">{temPix ? 'Pagamento via PIX' : 'Pagamento com cartão'}</Typography>
          <Typography variant="h4" fontWeight={700}>
            {formatarBRL(valorCentavos / 100)}
          </Typography>

          {fase === 'iniciando' && (
            <>
              <CircularProgress />
              <Typography color="text.secondary">Gerando o PIX…</Typography>
            </>
          )}

          {fase === 'pix' && (
            <>
              {pix?.encodedImage ? (
                <Box
                  component="img"
                  src={`data:image/png;base64,${pix.encodedImage}`}
                  alt="QR code PIX"
                  sx={{ width: 220, height: 220 }}
                />
              ) : pix?.payload ? (
                <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 1 }}>
                  <QRCodeSVG value={pix.payload} size={200} />
                </Box>
              ) : (
                <CircularProgress />
              )}
              <Typography variant="body2" color="text.secondary">
                Abra o app do seu banco, escaneie o QR ou use o copia-e-cola. A confirmação é automática.
              </Typography>
              {pix?.payload && (
                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                  <TextField value={pix.payload} size="small" fullWidth InputProps={{ readOnly: true }} />
                  <Button onClick={copiar} variant="outlined" startIcon={<ContentCopyIcon />} sx={{ flexShrink: 0 }}>
                    {copiado ? 'Copiado' : 'Copiar'}
                  </Button>
                </Stack>
              )}
              {/* PARADO (a pedido): botão "Pagar pelo app do meu banco" (Pix via Open Finance)
                  que abria a `invoiceUrl` do ASAAS. O backend ainda retorna `invoiceUrl` no
                  resultado do /pagar — pra reativar, restaurar este bloco usando r.invoiceUrl. */}
              {restantePixSeg !== null && (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={restantePixSeg <= 60 ? 'error.main' : 'text.primary'}
                >
                  {restantePixSeg > 0
                    ? `Este PIX expira em ${formatarTempoRestante(restantePixSeg)}`
                    : 'Prazo do PIX esgotado — aguarde…'}
                </Typography>
              )}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Aguardando o pagamento…</Typography>
              </Stack>
              {botaoOutraForma}
              <Button color="inherit" onClick={onCancelar} disabled={ocupado}>
                Cancelar pedido
              </Button>
            </>
          )}

          {fase === 'cartao' && (
            <Stack spacing={2} sx={{ width: '100%' }}>
              {temCartoesSalvos && (
                <RadioGroup
                  value={cartaoSel === null ? '' : String(cartaoSel)}
                  onChange={(e) => setCartaoSel(e.target.value === 'novo' ? 'novo' : Number(e.target.value))}
                >
                  {(cartoesSalvos ?? []).map((c) => (
                    <FormControlLabel
                      key={c.id}
                      value={String(c.id)}
                      control={<Radio />}
                      label={`${c.bandeira ?? 'Cartão'} •••• ${c.last4 ?? '----'}`}
                    />
                  ))}
                  <FormControlLabel value="novo" control={<Radio />} label="Usar outro cartão" />
                </RadioGroup>
              )}

              {usandoNovoCartao && (
                <>
                  <TextField
                    label="Número do cartão"
                    value={numeroCartao}
                    onChange={(e) => setNumeroCartao(formatarNumeroCartao(e.target.value))}
                    inputProps={{ inputMode: 'numeric' }}
                    autoComplete="cc-number"
                    fullWidth
                    autoFocus
                  />
                  <TextField
                    label="Nome impresso no cartão"
                    value={nomeCartao}
                    onChange={(e) => setNomeCartao(e.target.value.toUpperCase())}
                    autoComplete="cc-name"
                    fullWidth
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Validade (MM/AA)"
                      value={validade}
                      onChange={(e) => setValidade(formatarValidade(e.target.value))}
                      inputProps={{ inputMode: 'numeric' }}
                      autoComplete="cc-exp"
                      fullWidth
                    />
                    <TextField
                      label="CVV"
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      inputProps={{ inputMode: 'numeric' }}
                      autoComplete="cc-csc"
                      fullWidth
                    />
                  </Stack>
                  {logado && (
                    <FormControlLabel
                      control={<Checkbox checked={salvarCartao} onChange={(e) => setSalvarCartao(e.target.checked)} />}
                      label={<Typography variant="body2">Salvar este cartão para a próxima compra</Typography>}
                    />
                  )}
                </>
              )}

              {mensagem && <Alert severity="error">{mensagem}</Alert>}

              <Button variant="contained" onClick={pagarCartao} disabled={!podePagarCartao || statusPagar.isLoading}>
                {statusPagar.isLoading ? <CircularProgress size={20} /> : `Pagar ${formatarBRL(valorCentavos / 100)}`}
              </Button>
              {botaoOutraForma}
              <Button color="inherit" onClick={onCancelar} disabled={statusPagar.isLoading || ocupado}>
                Cancelar pedido
              </Button>
            </Stack>
          )}

          {fase === 'expirado' && (
            <>
              <Box sx={{ color: 'error.main', display: 'flex' }}>
                <ErrorOutlineIcon fontSize="large" />
              </Box>
              <Typography color="error">
                Este pedido foi cancelado e não pode mais ser pago. Refaça o pedido para pagar novamente.
              </Typography>
              <Button variant="contained" onClick={onRefazer}>
                Refazer pedido
              </Button>
            </>
          )}

          {fase === 'erro' && (
            <>
              <Box sx={{ color: 'error.main', display: 'flex' }}>
                <ErrorOutlineIcon fontSize="large" />
              </Box>
              <Typography color="error">{mensagem}</Typography>
              <Stack spacing={1} sx={{ width: '100%' }}>
                {temPix && (
                  <Button variant="contained" onClick={() => void iniciarPix()}>
                    Tentar novamente
                  </Button>
                )}
                {botaoOutraForma}
                <Button color="inherit" onClick={onCancelar} disabled={ocupado}>
                  Cancelar pedido
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

/** Agrupa o número do cartão em blocos de 4 (máx. 19 dígitos). */
function formatarNumeroCartao(valor: string): string {
  return valor
    .replace(/\D/g, '')
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

/** Formata a validade como MM/AA enquanto digita. */
function formatarValidade(valor: string): string {
  const d = valor.replace(/\D/g, '').slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
}

function extrairMsg(err: unknown): string | null {
  if (typeof err === 'object' && err && 'data' in err) {
    const d = (err as { data?: { mensagem?: string } }).data;
    return d?.mensagem ?? null;
  }
  return null;
}

function extrairCodigo(err: unknown): string | null {
  if (typeof err === 'object' && err && 'data' in err) {
    const d = (err as { data?: { codigo?: string } }).data;
    return d?.codigo ?? null;
  }
  return null;
}
